import type { Screen0LanguageCode } from "@/features/language/language-registry";
import {
  formatDisplayWindow,
  formatDurationMinutes as formatLocalizedDurationMinutes,
} from "../format";
import type { RuleConfig, RuleDraft, RuleWindow, TargetApp, TargetConfig, TargetDraft, TargetSelection } from "./ruleTypes";
import { hasNewTypedWebContent } from "./webTargetDelta";
import { resolveTargetDomains, sanitizeDomainCategories } from "./websiteCategories";

export const MINUTES_PER_DAY = 24 * 60;
export const MAX_TARGETS_PER_RULE = 3;
/** All weekdays, JS getDay() convention (0=Sun … 6=Sat). */
export const ALL_WEEKDAYS: number[] = [0, 1, 2, 3, 4, 5, 6];

/** Display order (Monday-first) with the stored JS getDay() value for each weekday. */
export const WEEKDAYS_DISPLAY: ReadonlyArray<{ value: number; short: string; long: string }> = [
  { value: 1, short: "M", long: "Monday" },
  { value: 2, short: "T", long: "Tuesday" },
  { value: 3, short: "W", long: "Wednesday" },
  { value: 4, short: "T", long: "Thursday" },
  { value: 5, short: "F", long: "Friday" },
  { value: 6, short: "S", long: "Saturday" },
  { value: 0, short: "S", long: "Sunday" },
];

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function normalizeDomain(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0]
    .replace(/[^a-z0-9.-]/g, "");
}

export function normalizeDomainTarget(value: string) {
  const domain = normalizeDomain(value);
  if (!domain || !domain.includes(".") || domain.startsWith(".") || domain.endsWith(".")) return "";
  return domain;
}

/** Stable namespaced key for a target app, matching the Habits search corpus ("app:<raw>"). */
export function targetAppKey(app: { appIdentifier?: string; displayName: string }): string {
  const raw = app.appIdentifier?.trim() || app.displayName.trim();
  return `app:${raw}`;
}

/**
 * Clamp a window to a same-day range: startMinute 0–1439, duration >= 1, and the
 * window is capped at end-of-day so it never crosses midnight. An end ≤ start
 * (non-positive duration) collapses to the 1-minute minimum.
 */
export function normalizeRuleWindow(window: RuleWindow): RuleWindow {
  const startMinute = clamp(Math.round(window.startMinute), 0, MINUTES_PER_DAY - 1);
  const maxDuration = MINUTES_PER_DAY - startMinute;
  const durationMinutes = clamp(Math.round(window.durationMinutes), 1, maxDuration);
  return { startMinute, durationMinutes };
}

export const MAX_QUICK_SESSION_DURATION_MINUTES = 300;

/**
 * Clamp a quick/instant session window: startMinute 0–1439, duration clamped to
 * [1, MAX_QUICK_SESSION_DURATION_MINUTES]. Unlike normalizeRuleWindow, this is
 * NOT capped at end-of-day — a quick session may extend past midnight.
 */
export function normalizeQuickSessionWindow(window: RuleWindow): RuleWindow {
  const startMinute = clamp(Math.round(window.startMinute), 0, MINUTES_PER_DAY - 1);
  const durationMinutes = clamp(Math.round(window.durationMinutes), 1, MAX_QUICK_SESSION_DURATION_MINUTES);
  return { startMinute, durationMinutes };
}

export function windowEndMinute(window: RuleWindow): number {
  return window.startMinute + window.durationMinutes;
}

/** Build a normalized window from a start + end minute-of-day pair (start+end input mode). */
export function windowFromStartEnd(startMinute: number, endMinute: number): RuleWindow {
  return normalizeRuleWindow({ startMinute, durationMinutes: endMinute - startMinute });
}

/** Map a Smart Block hour range (startHour inclusive, endHour exclusive) to a window. */
export function windowFromHours(startHour: number, endHour: number): RuleWindow {
  return normalizeRuleWindow({ startMinute: startHour * 60, durationMinutes: (endHour - startHour) * 60 });
}

/** Half-open interval overlap: [startA, endA) vs [startB, endB). Adjacent windows do not overlap. */
export function windowsOverlapHalfOpen(
  startA: number,
  endA: number,
  startB: number,
  endB: number,
): boolean {
  return startA < endB && startB < endA;
}

export function blockWindowsOverlap(
  a: { startHour: number; endHour: number },
  b: { startHour: number; endHour: number },
): boolean {
  return windowsOverlapHalfOpen(a.startHour, a.endHour, b.startHour, b.endHour);
}

export function ruleWindowOverlapsBlockWindow(
  ruleWindow: RuleWindow,
  blockWindow: { startHour: number; endHour: number },
): boolean {
  const ruleStart = ruleWindow.startMinute;
  const ruleEnd = ruleWindow.startMinute + ruleWindow.durationMinutes;
  const blockStart = blockWindow.startHour * 60;
  const blockEnd = blockWindow.endHour * 60;
  return windowsOverlapHalfOpen(ruleStart, ruleEnd, blockStart, blockEnd);
}

/** Empty selection defaults to every day (a rule with no active days could never fire). */
export function normalizeDays(days: number[]): number[] {
  const valid = new Set(days.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6));
  if (valid.size === 0) return [...ALL_WEEKDAYS];
  return [...valid].sort((a, b) => a - b);
}

export function emptyTargetDraft(): TargetDraft {
  return { name: "", apps: [], domains: [], domainCategories: [] };
}

export function emptyRuleDraft(): RuleDraft {
  return {
    enabled: true,
    targetIds: [],
    days: [...ALL_WEEKDAYS],
    // Spec default example: 16:00 for 30 minutes.
    window: { startMinute: 16 * 60, durationMinutes: 30 },
  };
}

function dedupeApps(apps: TargetApp[]): TargetApp[] {
  const seen = new Set<string>();
  const result: TargetApp[] = [];
  for (const app of apps) {
    const key = app.key.trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push({ ...app, key, label: app.label.trim() || key });
  }
  return result;
}

/** A picker selection counts as content only when it actually blocks something. */
export function selectionHasContent(selection: TargetDraft["selection"]): boolean {
  if (!selection) return false;
  return selection.applicationCount + selection.categoryCount + selection.webDomainCount > 0;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Human summary of picker apps/categories only (sites belong in the Websites section). */
export function selectionAppsCategoriesSummary(selection: TargetSelection): string {
  const parts: string[] = [];
  if (selection.applicationCount > 0) parts.push(plural(selection.applicationCount, "app", "apps"));
  if (selection.categoryCount > 0) parts.push(plural(selection.categoryCount, "category", "categories"));
  return parts.length > 0 ? parts.join(" · ") : "No apps selected";
}

/** Human summary of a picker selection alone, e.g. "3 apps · 1 category · 2 sites". */
export function selectionSummary(selection: TargetSelection): string {
  const parts: string[] = [];
  if (selection.applicationCount > 0) parts.push(plural(selection.applicationCount, "app", "apps"));
  if (selection.categoryCount > 0) parts.push(plural(selection.categoryCount, "category", "categories"));
  if (selection.webDomainCount > 0) parts.push(plural(selection.webDomainCount, "site", "sites"));
  return parts.length > 0 ? parts.join(" · ") : "No apps selected";
}

export type TargetContentSummaryOptions = {
  /** Smart Block targets store the same app in typed apps and the iOS picker — avoid double-counting. */
  dedupeTypedAndPickerApps?: boolean;
  /** Android native: typed apps and picker counts refer to the same packages — always dedupe. */
  androidNative?: boolean;
};

/**
 * Combined human summary of everything a target blocks: typed apps/domains plus the
 * picker selection's app/category/site counts. Target cards and rule previews use
 * this so a picker-only target never renders "Empty". Returns "Empty" only when the
 * target has no blockable content at all.
 */
export function targetContentSummary(
  target: Pick<TargetConfig, "apps" | "domains" | "domainCategories" | "selection">,
  options?: TargetContentSummaryOptions,
): string {
  const pickerAppCount = target.selection?.applicationCount ?? 0;
  let appCount: number;
  if (options?.androidNative) {
    appCount = target.apps.length > 0 ? target.apps.length : pickerAppCount;
  } else if (options?.dedupeTypedAndPickerApps && target.apps.length > 0 && pickerAppCount > 0) {
    appCount = Math.max(target.apps.length, pickerAppCount);
  } else {
    appCount = target.apps.length + pickerAppCount;
  }
  const categoryCount = target.selection?.categoryCount ?? 0;
  const siteCount = resolveTargetDomains(target).length + (target.selection?.webDomainCount ?? 0);
  const parts: string[] = [];
  if (appCount > 0) parts.push(plural(appCount, "app", "apps"));
  if (categoryCount > 0) parts.push(plural(categoryCount, "category", "categories"));
  if (siteCount > 0) parts.push(plural(siteCount, "website", "websites"));
  return parts.join(" · ") || "Empty";
}

export function sanitizeTargetDraft(draft: TargetDraft): TargetDraft {
  const domains = [...new Set(draft.domains.map(normalizeDomainTarget).filter(Boolean))];
  const domainCategories = sanitizeDomainCategories(draft.domainCategories);
  const apps = dedupeApps(draft.apps);
  const sanitized: TargetDraft = { name: draft.name.trim(), apps, domains, domainCategories };
  if (selectionHasContent(draft.selection)) sanitized.selection = draft.selection;
  if (draft._id) sanitized._id = draft._id;
  return sanitized;
}

/** Convex `targets:upsert` accepts only name/apps/domains/selection — never pass `_id` inside `target`. */
export function targetConfigFromDraft(draft: TargetDraft): TargetConfig {
  const { name, apps, domains, domainCategories = [], selection } = sanitizeTargetDraft(draft);
  const config: TargetConfig = { name, apps, domains };
  if (domainCategories.length > 0) config.domainCategories = domainCategories;
  if (selection) config.selection = selection;
  return config;
}

export type TargetDraftValidation = {
  nameRequired: boolean;
  contentRequired: boolean;
  vpnRequiredForWebAdditions: boolean;
};

export function validateTargetDraft(
  draft: TargetDraft,
  options?: { baseline?: TargetDraft; needsVpnForTypedWeb?: boolean },
): TargetDraftValidation {
  const sanitized = sanitizeTargetDraft(draft);
  const baseline = options?.baseline ?? emptyTargetDraft();
  const needsVpn = options?.needsVpnForTypedWeb ?? false;
  return {
    nameRequired: sanitized.name.length === 0,
    contentRequired:
      sanitized.apps.length === 0 &&
      sanitized.domains.length === 0 &&
      (sanitized.domainCategories ?? []).length === 0 &&
      !selectionHasContent(sanitized.selection),
    vpnRequiredForWebAdditions: needsVpn && hasNewTypedWebContent(baseline, sanitized),
  };
}

/** A Target must be named and contain at least one app or domain. */
export function isTargetDraftSaveable(draft: TargetDraft): boolean {
  const validation = validateTargetDraft(draft);
  return !validation.nameRequired && !validation.contentRequired;
}

export function sanitizeRuleDraft(draft: RuleDraft): RuleDraft {
  const sanitized: RuleDraft = {
    enabled: draft.enabled,
    targetIds: [...new Set(draft.targetIds)].slice(0, MAX_TARGETS_PER_RULE),
    days: normalizeDays(draft.days),
    window: normalizeRuleWindow(draft.window),
  };
  if (draft._id) sanitized._id = draft._id;
  return sanitized;
}

/** Convex `rules:upsert` accepts only schedule fields — never pass `_id` inside `rule`. */
export function ruleConfigFromDraft(draft: RuleDraft): RuleConfig {
  const { enabled, targetIds, days, window } = sanitizeRuleDraft(draft);
  return { enabled, targetIds, days, window };
}

/** A rule needs at least one target to enforce anything. */
export function isRuleDraftSaveable(draft: RuleDraft): boolean {
  return sanitizeRuleDraft(draft).targetIds.length > 0;
}

/** Internal minute-of-day as HH:MM (1440 → "24:00"); use formatDisplayMinuteOfDay for UI. */
export function formatMinuteOfDay(minute: number): string {
  if (minute >= MINUTES_PER_DAY) return "24:00";
  return `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`;
}

export function parseMinuteOfDay(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  return clamp(hours * 60 + minutes, 0, MINUTES_PER_DAY);
}

export function formatWindow(window: RuleWindow, languageCode: Screen0LanguageCode = "en"): string {
  return formatDisplayWindow(window, languageCode);
}

export function formatDurationMinutes(
  durationMinutes: number,
  languageCode: Screen0LanguageCode = "en",
): string {
  return formatLocalizedDurationMinutes(durationMinutes, languageCode);
}

function sameDaySet(days: number[], other: number[]): boolean {
  if (days.length !== other.length) return false;
  const set = new Set(days);
  return other.every((day) => set.has(day));
}

export function formatDays(days: number[]): string {
  const normalized = normalizeDays(days);
  if (normalized.length === 7) return "Every day";
  if (sameDaySet(normalized, [1, 2, 3, 4, 5])) return "Weekdays";
  if (sameDaySet(normalized, [0, 6])) return "Weekends";
  return WEEKDAYS_DISPLAY.filter((day) => normalized.includes(day.value))
    .map((day) => day.long.slice(0, 3))
    .join(", ");
}
